import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Hash, User, FileText, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { detectSearchType, type SearchDetection } from '@/lib/searchUtils';
import { useProfileSearch } from '@/hooks/useProfileSearch';
import { nip19 } from 'nostr-tools';
import { cn } from '@/lib/utils';

interface SearchBarProps {
  className?: string;
  placeholder?: string;
  autoFocus?: boolean;
}

export function SearchBar({
  className,
  placeholder = 'Search creators or paste npub...',
  autoFocus = false,
}: SearchBarProps) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Debounce search query - reduced to 150ms for faster response
  // Local searches are instant, remote queries timeout at 1500ms
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query);
    }, 150);

    return () => clearTimeout(timer);
  }, [query]);

  // Detect search type
  const detection: SearchDetection = detectSearchType(debouncedQuery);

  // Only search for profiles if it's a text/profile search
  const shouldSearchProfiles =
    detection.type.includes('profile') && !detection.isHex && !detection.isNip19;

  // Profile search returns local results immediately (instant)
  // Remote results are fetched in background for cache warming
  const { data: profileResults, isLoading: isLoadingProfiles } = useProfileSearch(
    debouncedQuery,
    shouldSearchProfiles
  );

  // Build dropdown options
  const options: Array<{
    type: 'profile' | 'text' | 'hashtag' | 'note' | 'direct';
    label: string;
    value: string;
    icon: React.ReactNode;
    metadata?: any;
  }> = [];

  // Handle direct NIP-19 or hex navigation
  if (detection.isNip19 || detection.isHex) {
    const icon = detection.type.includes('profile') ? (
      <User className="h-4 w-4" />
    ) : (
      <FileText className="h-4 w-4" />
    );

    options.push({
      type: 'direct',
      label: detection.isHex
        ? `Open ${detection.type.join('/')} by ID`
        : `Open ${detection.nip19Type}`,
      value: detection.value,
      icon,
    });
  }

  // Add profile results
  if (profileResults && profileResults.length > 0) {
    profileResults.forEach((result) => {
      const displayName =
        result.metadata.display_name || result.metadata.name || 'Anonymous';

      options.push({
        type: 'profile',
        label: displayName,
        value: result.pubkey,
        icon: (
          <Avatar className="h-6 w-6 rounded-lg">
            <AvatarImage src={result.metadata.picture} />
            <AvatarFallback className="rounded-lg text-xs">
              {displayName[0].toUpperCase()}
            </AvatarFallback>
          </Avatar>
        ),
        metadata: result.metadata,
      });
    });
  }

  // Add hashtag search option
  if (detection.type.includes('hashtag')) {
    options.push({
      type: 'hashtag',
      label: `Search #${detection.value}`,
      value: detection.value,
      icon: <Hash className="h-4 w-4" />,
    });
  }

  // Add text search option
  if (
    detection.type.includes('text') &&
    debouncedQuery.trim() &&
    !detection.isHex &&
    !detection.isNip19
  ) {
    options.push({
      type: 'text',
      label: `Search for "${debouncedQuery}"`,
      value: debouncedQuery,
      icon: <Search className="h-4 w-4" />,
    });
  }

  // Show dropdown when there are options and input is focused
  const hasOptions = options.length > 0;
  const shouldShowDropdown = showDropdown && hasOptions && query.trim();

  // Navigate to selected option
  const navigateToOption = useCallback(
    (option: (typeof options)[0]) => {
      if (option.type === 'profile') {
        const npub = nip19.npubEncode(option.value);
        navigate(`/${npub}`);
      } else if (option.type === 'direct') {
        if (detection.isNip19) {
          navigate(`/${option.value}`);
        } else if (detection.isHex) {
          // Try as npub first
          const npub = nip19.npubEncode(option.value);
          navigate(`/${npub}`);
        }
      } else if (option.type === 'hashtag') {
        navigate(`/search?q=${encodeURIComponent('#' + option.value)}`);
      } else if (option.type === 'text') {
        navigate(`/search?q=${encodeURIComponent(option.value)}`);
      }

      // Clear and close
      setQuery('');
      setShowDropdown(false);
      inputRef.current?.blur();
    },
    [navigate, detection]
  );

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!shouldShowDropdown) {
      if (e.key === 'Enter' && query.trim()) {
        // Default enter behavior - go to search page
        navigate(`/search?q=${encodeURIComponent(query)}`);
        setQuery('');
        setShowDropdown(false);
        inputRef.current?.blur();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) => (prev + 1) % options.length);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) => (prev - 1 + options.length) % options.length);
        break;
      case 'Enter':
        e.preventDefault();
        if (options[selectedIndex]) {
          navigateToOption(options[selectedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setShowDropdown(false);
        inputRef.current?.blur();
        break;
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(e.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Reset selected index when options change
  useEffect(() => {
    setSelectedIndex(0);
  }, [options.length]);

  return (
    <div className={cn('relative', className)}>
      <div className="relative group">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground z-10" />
        <Input
          ref={inputRef}
          placeholder={placeholder}
          className="relative pl-11 pr-10 h-10 text-sm bg-secondary/50 border-0 rounded-xl focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:ring-offset-0 transition-all"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => setShowDropdown(true)}
          autoFocus={autoFocus}
        />
        {query && (
          <button
            onClick={() => {
              setQuery('');
              inputRef.current?.focus();
            }}
            className="absolute right-3 top-1/2 -translate-y-1/2 h-6 w-6 flex items-center justify-center rounded-lg hover:bg-secondary transition-colors z-10"
          >
            <X className="h-3.5 w-3.5 text-muted-foreground" />
          </button>
        )}
      </div>

      {/* Dropdown */}
      {shouldShowDropdown && (
        <Card
          ref={dropdownRef}
          className="absolute top-full mt-2 w-full shadow-lg border-0 rounded-xl overflow-hidden z-50"
        >
          <CardContent className="p-0">
            <div className="max-h-96 overflow-y-auto">
              {isLoadingProfiles && shouldSearchProfiles ? (
                <div className="px-4 py-6 text-center text-sm text-muted-foreground">
                  Searching...
                </div>
              ) : (
                options.map((option, index) => (
                  <button
                    key={`${option.type}-${option.value}-${index}`}
                    onClick={() => navigateToOption(option)}
                    className={cn(
                      'w-full px-4 py-3 flex items-center gap-3 hover:bg-secondary/50 transition-colors text-left',
                      selectedIndex === index && 'bg-secondary/50'
                    )}
                  >
                    <div className="flex-shrink-0 text-muted-foreground">
                      {option.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm truncate">{option.label}</div>
                      {option.metadata?.nip05 && (
                        <div className="text-xs text-primary truncate">
                          {option.metadata.nip05}
                        </div>
                      )}
                    </div>
                  </button>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
